import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MSRoadMapManager } from '../utils/MSRoadMapManager';
import type { M365ReleaseItem, M365SortField, M365TagContainer } from '../types/m365';
import M365UpdateFilter, { type M365UpdateFilters } from '../components/M365UpdateFilter';
import M365UpdateList from '../components/M365UpdateList';

const defaultFilters: M365UpdateFilters = {
    createdStart: '',
    createdEnd: '',
    modifiedStart: '',
    modifiedEnd: '',
    products: [],
    releasePhase: [],
    platforms: [],
    status: []
};

function parseDate(s: string): number {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function applyFilters(items: M365ReleaseItem[], filters: M365UpdateFilters): M365ReleaseItem[] {
    return items.filter((item) => {
        if (filters.createdStart) {
            const created = parseDate(item.created ?? '');
            if (created < parseDate(filters.createdStart)) return false;
        }
        if (filters.createdEnd) {
            const created = parseDate(item.created ?? '');
            if (created > parseDate(filters.createdEnd + 'T23:59:59')) return false;
        }
        if (filters.modifiedStart) {
            const modified = parseDate(item.modified ?? '');
            if (modified < parseDate(filters.modifiedStart)) return false;
        }
        if (filters.modifiedEnd) {
            const modified = parseDate(item.modified ?? '');
            if (modified > parseDate(filters.modifiedEnd + 'T23:59:59')) return false;
        }
        if (filters.products.length > 0) {
            const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
            const products = container?.products ?? [];
            const names = new Set(products.map((p) => MSRoadMapManager.getInstance().getProductDisplayName(p)));
            const hasMatch = filters.products.some((name) => names.has(name));
            if (!hasMatch) return false;
        }
        if (filters.releasePhase.length > 0) {
            const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
            const releasePhase = container?.releasePhase;
            if (!Array.isArray(releasePhase)) return false;
            const phaseNames = new Set(
                releasePhase.map((entry) => {
                    const o = entry as { tagName?: string };
                    return typeof o?.tagName === 'string' ? o.tagName.trim() : '';
                }).filter(Boolean)
            );
            const hasMatch = filters.releasePhase.some((name) => phaseNames.has(name));
            if (!hasMatch) return false;
        }
        if (filters.platforms.length > 0) {
            const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
            const platforms = container?.platforms;
            if (!Array.isArray(platforms)) return false;
            const platformNames = new Set(
                platforms.map((entry) => {
                    const o = entry as { tagName?: string };
                    return typeof o?.tagName === 'string' ? o.tagName.trim() : '';
                }).filter(Boolean)
            );
            const hasMatch = filters.platforms.some((name) => platformNames.has(name));
            if (!hasMatch) return false;
        }
        if (filters.status.length > 0) {
            const itemStatus = typeof item.status === 'string' ? item.status.trim() : '';
            if (!filters.status.includes(itemStatus)) return false;
        }
        return true;
    });
}

function M365Updates() {
    const [searchParams] = useSearchParams();
    const statusParam = searchParams.get('status');
    const productParam = searchParams.get('product');

    const [allItems, setAllItems] = useState<M365ReleaseItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<M365UpdateFilters>(() => ({
        ...defaultFilters,
        status: statusParam ? [statusParam] : [],
        products: productParam ? [productParam] : []
    }));

    useEffect(() => {
        if (statusParam != null && statusParam !== '') {
            setFilters((prev) => {
                if (prev.status.length === 1 && prev.status[0] === statusParam) return prev;
                return { ...prev, status: [statusParam] };
            });
        }
    }, [statusParam]);

    useEffect(() => {
        if (productParam != null && productParam !== '') {
            setFilters((prev) => {
                if (prev.products.length === 1 && prev.products[0] === productParam) return prev;
                return { ...prev, products: [productParam] };
            });
        }
    }, [productParam]);

    const productOptions = useMemo(
        () => MSRoadMapManager.getInstance().getUniqueProducts(allItems),
        [allItems]
    );
    const releasePhaseOptions = useMemo(
        () => MSRoadMapManager.getInstance().getUniqueReleasePhases(allItems),
        [allItems]
    );
    const platformOptions = useMemo(
        () => MSRoadMapManager.getInstance().getUniquePlatforms(allItems),
        [allItems]
    );
    const statusOptions = useMemo(
        () => MSRoadMapManager.getInstance().getUniqueStatuses(allItems),
        [allItems]
    );
    const filteredItems = useMemo(
        () => applyFilters(allItems, filters),
        [allItems, filters]
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        MSRoadMapManager.getInstance().fetchM365Releases()
            .then((data) => {
                if (!cancelled) setAllItems(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load releases');
                    setAllItems([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const handleSearch = useCallback((searchFilters: M365UpdateFilters) => {
        setFilters(searchFilters);
    }, []);

    return (
        <div className="m365-updates-page video-archive-page">
            <div className="page-title">
                <div className="container">
                    <h1>Microsoft 365 releases &amp; feature updates</h1>
                </div>
            </div>

            <section className="section">
                <div className="container">
                    <div className="video-archive-layout">
                        <aside className="video-archive-filter-panel">
                            <M365UpdateFilter
                                filters={filters}
                                onFiltersChange={setFilters}
                                onSearch={handleSearch}
                                loading={loading}
                                productOptions={productOptions}
                                releasePhaseOptions={releasePhaseOptions}
                                platformOptions={platformOptions}
                                statusOptions={statusOptions}
                            />
                        </aside>
                        <div className="video-archive-results-panel">
                            <M365UpdateList
                                items={filteredItems}
                                loading={loading}
                                error={error}
                                maxItems={10}
                                sortField={'modified' as M365SortField}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default M365Updates;
